/**
 * A box of words made narrower or wider by a grip on its side (2026-10-28, Adam: "the size of the
 * containing box is always perfect for the text that is inside"; "the size goes beyond the finger").
 *
 * The finger says how wide the words may be (`handWidth`): the side in the hand is where the finger
 * is — never off the plan — and the other side goes out or in with it about the box's middle, or, held
 * by the plan's edge, stays there. The box is then as wide as its words as they wrap within that, so
 * the side in the hand never goes beyond the finger, and it stands on the box's middle, on the plan
 * (`boxLeft`). When its words wrap otherwise its frame glides to the new width (`Glide`), never a jump.
 */

/** How long a frame takes to glide to a box's new width. */
export const GLIDE_MS = 160;

/**
 * The width a box's side in the hand gives its words: the box `rect` was (plan px), its side `side`
 * (0 left, 1 right) carried to `finger` (its x), the plan being `lo`…`hi` across. About the middle
 * where the box has room; the other side held by the plan's edge where it has not.
 */
export function handWidth(rect: { x: number; w: number }, side: 0 | 1, finger: number, lo: number, hi: number): number {
  const cx = rect.x + rect.w / 2;
  if (side === 1) {
    const f = Math.min(hi, finger);
    // (the left side as far out as the right, unless the plan's edge holds it)
    return Math.max(0, f - Math.max(lo, 2 * cx - f));
  }
  const f = Math.max(lo, finger);
  return Math.max(0, Math.min(hi, 2 * cx - f) - f);
}

/** Where a box `w` wide stands across: on the middle `cx`, wholly on the plan `lo`…`hi`. */
export function boxLeft(cx: number, w: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi - w, cx - w / 2));
}

/** A frame across (plan px): its left side and its width. */
export interface Span {
  x: number;
  w: number;
}
/** A frame gliding from where it was drawn to where it is going, from `t0` (ms). */
export interface Glide {
  from: Span;
  to: Span;
  t0: number;
}

/**
 * Where a gliding frame is at `t` (ms) — eased out, so it moves at once and settles softly, always
 * between where it was and where it is going (never past either: no width it passes on its way is
 * one its words would not fit).
 */
export function glideAt(g: Glide, t: number): Span {
  const p = Math.max(0, Math.min(1, (t - g.t0) / GLIDE_MS));
  const e = 1 - (1 - p) ** 3;
  return { x: g.from.x + (g.to.x - g.from.x) * e, w: g.from.w + (g.to.w - g.from.w) * e };
}

/** Whether a frame has come to where it was going by `t`. */
export function glided(g: Glide, t: number): boolean {
  return t - g.t0 >= GLIDE_MS;
}

/** The glide to `to` from wherever the frame is at `t` (where it is drawn now: no jump). */
export function glideTo(g: Glide | null, at: Span, to: Span, t: number): Glide {
  return { from: g ? glideAt(g, t) : at, to, t0: t };
}
